import React from "react";

/**
 * Reusable colorful page header (with subtle gradient ornament).
 * tone: "brand" | "sph" | "invoice" | "success" | "violet" | "neutral"
 */
const TONES = {
  brand: {
    bg: "bg-gradient-to-br from-brand/10 via-sky-50 to-white",
    chip: "bg-brand text-white",
    overline: "text-brand-700",
    ornament:
      "before:absolute before:-top-20 before:right-10 before:h-56 before:w-56 before:rounded-full before:bg-brand/15 before:blur-3xl",
  },
  sph: {
    bg: "bg-gradient-to-br from-sky-100/70 via-sky-50 to-white",
    chip: "bg-sky-500 text-white",
    overline: "text-sky-700",
    ornament:
      "before:absolute before:-top-20 before:right-10 before:h-56 before:w-56 before:rounded-full before:bg-sky-300/40 before:blur-3xl",
  },
  invoice: {
    bg: "bg-gradient-to-br from-amber-100/70 via-amber-50 to-white",
    chip: "bg-amber-500 text-white",
    overline: "text-amber-700",
    ornament:
      "before:absolute before:-top-20 before:right-10 before:h-56 before:w-56 before:rounded-full before:bg-amber-300/40 before:blur-3xl",
  },
  success: {
    bg: "bg-gradient-to-br from-emerald-100/70 via-emerald-50 to-white",
    chip: "bg-emerald-500 text-white",
    overline: "text-emerald-700",
    ornament:
      "before:absolute before:-top-20 before:right-10 before:h-56 before:w-56 before:rounded-full before:bg-emerald-300/40 before:blur-3xl",
  },
  violet: {
    // teal-tinted (avoid purple/violet per design rule)
    bg: "bg-gradient-to-br from-teal-100/70 via-teal-50 to-white",
    chip: "bg-teal-600 text-white",
    overline: "text-teal-700",
    ornament:
      "before:absolute before:-top-20 before:right-10 before:h-56 before:w-56 before:rounded-full before:bg-teal-300/40 before:blur-3xl",
  },
  neutral: {
    bg: "bg-gradient-to-br from-slate-100/60 via-white to-white",
    chip: "bg-ink-900 text-white",
    overline: "text-ink-500",
    ornament:
      "before:absolute before:-top-20 before:right-10 before:h-56 before:w-56 before:rounded-full before:bg-slate-300/40 before:blur-3xl",
  },
};

export function PageHeader({
  overline,
  title,
  description,
  icon: Icon,
  tone = "brand",
  actions,
  testId,
}) {
  const t = TONES[tone] || TONES.brand;
  return (
    <header
      className={`relative overflow-hidden rounded-2xl border border-border p-6 sm:p-8 ${t.bg} ${t.ornament}`}
      data-testid={testId}
    >
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-start gap-4">
          {Icon && (
            <div className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl shadow-sm ${t.chip}`}>
              <Icon className="h-6 w-6" />
            </div>
          )}
          <div>
            {overline && (
              <p className={`overline ${t.overline}`}>{overline}</p>
            )}
            <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-ink-900 sm:text-4xl">
              {title}
            </h1>
            {description && (
              <p className="mt-2 max-w-2xl text-sm text-ink-500">{description}</p>
            )}
          </div>
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
    </header>
  );
}

export default PageHeader;
